import { useArrived, useTooltip, type TipRow } from './Tooltip'

// Part to whole at a glance: vendors by risk band as a 22px ring with a 3px
// surface gap between segments, the total in the middle, and a legend beside it
// that carries every count, so no value is only in the tooltip.
//
// Each segment is a link to the register filtered to that band, reachable by
// Tab, and focusing it shows the same tooltip pointing at it does.

export type DonutSlice = { key: string; label: string; count: number; color: string; href?: string }

const R = 56
const C = 2 * Math.PI * R
const GAP = 3

export function Donut({
  slices,
  total,
  unit,
  title,
}: {
  slices: DonutSlice[]
  total: number
  unit: string
  title: string
}) {
  const arrived = useArrived()
  const tip = useTooltip()
  const sum = slices.reduce((a, s) => a + s.count, 0)
  let offset = 0
  const counted = (n: number) => n + ' ' + (n === 1 ? unit.replace(/s$/, '') : unit)
  const rowsOf = (s: DonutSlice): TipRow[] => [{ label: s.label, value: counted(s.count), color: s.color }]

  return (
    <div className="hc-donut">
      <svg viewBox="0 0 150 150" role="group" aria-label={title}>
        <circle cx="75" cy="75" r={R} fill="none" stroke="var(--sunk)" strokeWidth="22" />
        {slices.map((s) => {
          const len = sum > 0 ? (s.count / sum) * C : 0
          const at = offset
          offset += len
          if (s.count === 0) return null
          const target = Math.max(len - (slices.filter((x) => x.count > 0).length > 1 ? GAP : 0), 0)
          const seg = (
            <circle
              className="seg"
              cx="75"
              cy="75"
              r={R}
              stroke={s.color}
              strokeDasharray={(arrived ? target : 0) + ' ' + C}
              strokeDashoffset={-at}
            />
          )
          const events = {
            onPointerMove: (e: React.PointerEvent) => tip.show(e, title, rowsOf(s)),
            onPointerLeave: () => tip.hide(),
            onFocus: (e: React.FocusEvent) => tip.showAt(e.currentTarget, title, rowsOf(s)),
            onBlur: () => tip.hide(),
          }
          return s.href ? (
            <a key={s.key} href={s.href} aria-label={s.label + ': ' + counted(s.count)} {...events}>
              {seg}
            </a>
          ) : (
            <g key={s.key} tabIndex={0} role="img" aria-label={s.label + ': ' + counted(s.count)} {...events}>
              {seg}
            </g>
          )
        })}
        {/* One sheen over every segment, lighter above and a shade deeper
            below, so the ring reads as a rounded band. It takes no pointer, so
            each segment keeps its tooltip and link. */}
        <defs>
          <linearGradient id="hc-donut-sheen" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#fff" stopOpacity="0.26" />
            <stop offset="0.5" stopColor="#fff" stopOpacity="0" />
            <stop offset="1" stopColor="#000" stopOpacity="0.1" />
          </linearGradient>
        </defs>
        <circle className="sheen" cx="75" cy="75" r={R} fill="none" stroke="url(#hc-donut-sheen)" strokeWidth="22" />
      </svg>
      <div className="c">
        <div>
          <b>{total}</b>
          <span>{unit}</span>
        </div>
      </div>
    </div>
  )
}

// A move is a vendor that stood in a different band at the start of the period.
export type BandMoves = Record<string, { in: number; out: number }>

// The legend carries every count, and beside a band any vendors that moved into
// it (+) or out of it (-) over the period, in ink rather than a status colour,
// because a vendor moving into Low is good news and into High is not, and the
// sign alone says which way it went.
export function Legend({ slices, moves, movesWords }: { slices: DonutSlice[]; moves?: BandMoves; movesWords?: string }) {
  return (
    <div className="hc-legend">
      {slices.map((s) => {
        const m = moves?.[s.key]
        const moved = m != null && (m.in > 0 || m.out > 0)
        const said =
          s.label +
          ': ' +
          s.count +
          (moved
            ? ', ' +
              [m.in > 0 ? m.in + ' moved in' : '', m.out > 0 ? m.out + ' moved out' : ''].filter(Boolean).join(' and ') +
              (movesWords ? ' ' + movesWords : '')
            : '')
        const inner = (
          <>
            <i style={{ background: s.color }} />
            <span>
              {s.label}
              {moved && (
                <span className="hc-moves" aria-hidden="true">
                  {m.in > 0 && <em className="in">+{m.in}</em>}
                  {m.out > 0 && <em className="out">-{m.out}</em>}
                </span>
              )}
            </span>
            <b className="hc-num">{s.count}</b>
          </>
        )
        return s.href ? (
          <a key={s.key} href={s.href} aria-label={said} title={moved ? said : undefined}>
            {inner}
          </a>
        ) : (
          <div key={s.key} aria-label={moved ? said : undefined} title={moved ? said : undefined}>
            {inner}
          </div>
        )
      })}
    </div>
  )
}
